import React from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import PageHero from '../components/layout/PageHero';

const ContentGuidelinesPage = () => {
  return (
    <div className="min-h-screen" data-testid="content-guidelines-page">
      <PageHero
        eyebrow="Community"
        title="Content Guidelines"
        description="The ground rules for articles, questions, and discussions on AUTOXIUM."
      />
      <section className="py-12 md:py-16">
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          <div className="max-w-3xl prose-autoxium">
            <h2>What Belongs Here</h2>
            <p>
              AUTOXIUM discussions and articles should stay focused on automotive engineering:
              vehicle systems, diagnostics, protocols (like CAN, UDS, AUTOSAR), tooling,
              career and industry topics, and hands-on repair or modification experience.
              General chit-chat is fine in moderation, but keep the signal-to-noise ratio
              high.
            </p>

            <h2>Accuracy &amp; Citations</h2>
            <p>
              We're a technical community, so precision matters. When you state a spec,
              standard, or procedure, cite where it comes from (a datasheet, standard
              number, OEM manual, or your own tested results) whenever you can. If you're
              speculating or working from memory, say so — it's fine to be uncertain, but
              don't present a guess as verified fact.
            </p>

            <h2>Respectful Disagreement</h2>
            <p>
              Technical disagreements are welcome and often useful. Attack the argument,
              not the person. No personal insults, harassment, or dismissiveness toward
              beginners — everyone on <Link to="/beginners-guide">the Beginner's Guide</Link>{' '}
              path was new once.
            </p>
          </div>

          <div className="max-w-3xl my-8 rounded-xl border border-amber-300 bg-amber-50 p-6 flex gap-4">
            <AlertTriangle className="h-6 w-6 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <p className="font-chivo text-base font-bold uppercase tracking-tight text-amber-900 mb-1">
                Safety Disclaimer
              </p>
              <p className="text-sm text-amber-900/90 leading-relaxed">
                Advice on vehicle modification, diagnostics, or repair can involve real
                safety and legal risk. Contributors and readers are responsible for
                verifying procedures against official service documentation and
                applicable regulations before acting on them. AUTOXIUM and its contributors
                are not liable for outcomes resulting from advice found on the
                platform — see our <Link to="/terms-of-service">Terms of Service</Link>.
              </p>
            </div>
          </div>

          <div className="max-w-3xl prose-autoxium">
            <h2>Moderation &amp; Reporting</h2>
            <p>
              Community questions and answers are moderated after the fact. If you see
              content that breaks these guidelines, use the <strong>Report</strong> button
              on the question or answer (or reach out via{' '}
              <Link to="/contact">Contact</Link>), and a moderator will review it. Repeated
              or serious violations can result in content removal or account suspension.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContentGuidelinesPage;
